import * as React from 'react'
import { Input } from 'antd'
import { THEME_COLORS } from '@getgreenline/shared'
import { UBER_EATS_DENY_REASONS } from '../../constants/UberEats'
import { UberEatsDenyReasonCode } from '../../models/UberEats/UberEatsModels'
import { isDenyDraftComplete } from '../../utilities/uberEatsIncomingOrder'
import { UberEatsActionButton } from './UberEatsActionButton'

interface Props {
  reasonCode: UberEatsDenyReasonCode | null
  explanation: string
  isInFlight: boolean
  isDenying: boolean
  onSelectReason: (code: UberEatsDenyReasonCode) => void
  onChangeExplanation: (explanation: string) => void
  onCancel: () => void
  onConfirm: () => void
}

const { BRAND, MONO } = THEME_COLORS

const chipStyle = (isSelected: boolean, isDisabled: boolean): React.CSSProperties => ({
  padding: '4px 12px',
  marginRight: 8,
  marginBottom: 8,
  borderRadius: 16,
  border: `1px solid ${isSelected ? BRAND.black : MONO.monocc}`,
  backgroundColor: isSelected ? BRAND.black : MONO.monoFf,
  color: isSelected ? MONO.monoFf : BRAND.black,
  cursor: isDisabled ? 'not-allowed' : 'pointer',
  opacity: isDisabled ? 0.5 : 1,
})

export const UberEatsDenyReasonPicker = ({
  reasonCode,
  explanation,
  isInFlight,
  isDenying,
  onSelectReason,
  onChangeExplanation,
  onCancel,
  onConfirm,
}: Props) => {
  const selected = UBER_EATS_DENY_REASONS.find(({ code }) => code === reasonCode)
  const canConfirm = isDenyDraftComplete({ reasonCode, explanation })

  return (
    <div role='group' aria-label='Reason for denying the order'>
      <div className='d-flex flex-wrap'>
        {UBER_EATS_DENY_REASONS.map(({ code, label }) => (
          <button
            key={code}
            type='button'
            aria-pressed={reasonCode === code}
            disabled={isInFlight}
            style={chipStyle(reasonCode === code, isInFlight)}
            onClick={() => onSelectReason(code)}
          >
            {label}
          </button>
        ))}
      </div>

      {selected?.requiresExplanation && (
        <Input
          className='mb-2'
          placeholder='Tell Uber Eats why'
          aria-label='Reason explanation'
          value={explanation}
          disabled={isInFlight}
          onChange={(event) => onChangeExplanation(event.target.value)}
        />
      )}

      <div className='d-flex' style={{ gap: 8 }}>
        <UberEatsActionButton title='Cancel' disabled={isInFlight} onClick={onCancel} />
        <UberEatsActionButton
          title={isDenying ? 'Denying…' : 'Confirm deny'}
          variant='confirm'
          disabled={!canConfirm || isInFlight}
          onClick={onConfirm}
        />
      </div>
    </div>
  )
}
